// SPDX-License-Identifier: GPL-2.0-or-later
/**
 * Fetch + cache for the tiered field catalogue `dist/field-catalog/` ( two
 * levels: index, then one detail file per BT/BG id - see build-field-catalog.php's own header for
 * why it exists: loading the whole 2.3 MB fields.mjs for a single lookup is too expensive ). Same
 * "one client, one baseUrl" shape as RuleCatalogClient.mjs, reachable at either `dist/field-catalog/`
 * (bundled) or `site-api/v1/fields*` (public API) - see that file's own header for the full reasoning,
 * not repeated here.
 */

export class FieldCatalogClient {

	/**
	 * ⚠️ A SECOND ROOT FOR THE FIELDS OF THE EXTENSION. Technical Details carries only the 311 fields of
	 * EN 16931; the "Extended Profiles" add-on carries the 963 fields of ZUGFeRD / Factur-X EXTENDED under
	 * `data/protected/field-catalog/`, in the same layout. Same mechanism as the second root of
	 * RuleCatalogClient.mjs: index() appends the entries of that root's own index.json that the first root
	 * does not list, remembers per field id which root it came from, and field() fetches from there. A
	 * missing or unreadable second root is the normal case without the add-on and is skipped silently - it
	 * must never cost the fields of the first root.
	 *
	 * ⚠️ EVERY ENTRY FROM THE SECOND ROOT CARRIES `source: 'protected'`. The field browser derives the
	 * scope filter from it. Deriving it from `-X-` in the id is wrong for 17 fields of the
	 * extension with a plain number ( BT-173 to BT-177, BT-179, BT-180, BT-186, BT-193, BT-215, BT-216,
	 * BG-34 and five sub-ids of these, measured 08.10.2026 ); it stays only as the fallback for a first
	 * root that still lists the extension itself ( an older Technical Details ).
	 *
	 * ⚠️ THE SECOND ROOT ALSO LISTS THE ELEVEN FIELDS OF THE XRechnung Extension AND OF
	 * EXTENDED-CTC-FR, each with a key `extension` that names its extension. The spread `{ ...e }` below
	 * passes it on unchanged; the field browser turns it into its own scope option ( scopeOf() there ).
	 *
	 * @param {string} baseUrl           e.g. ".../dist/field-catalog" or ".../site-api/v1/fields" (no trailing slash)
	 * @param {string} [protectedBaseUrl] the add-on's field-catalog folder, '' without the add-on
	 */
	constructor( baseUrl, protectedBaseUrl = '' ) {

		this._baseUrl = baseUrl.replace( /\/$/, '' );

		this._protectedBaseUrl = ( protectedBaseUrl || '' ).replace( /\/$/, '' );

		/** @type {Map<string, string>} field id -> the root its detail file is fetched from */
		this._bases = new Map();

		/** @type {Array<object>|null} */
		this._index = null;

		/** @type {Map<string, Promise<object>>} field id -> full detail */
		this._fields = new Map();

	}

	/** @return {Promise<Array<{id:string,businessTerm:object,cardinality?:string,kind?:string,appliesTo:object,source?:string}>>} */
	async index() {

		if ( this._index )
			return this._index;

		const res = await fetch( `${ this._baseUrl }/index.json` );

		if ( ! res.ok )
			throw new Error( `FieldCatalogClient: index.json failed (${ res.status })` );

		const data = await res.json();

		// ⚠️ A copy, not the parsed array itself: entries are appended below.
		const entries = [ ...( Array.isArray( data ) ? data : ( data.fields ?? [] ) ) ];

		if ( this._protectedBaseUrl !== '' ) {

			try {

				const extra = await fetch( `${ this._protectedBaseUrl }/index.json` );

				if ( extra.ok ) {

					const more  = await extra.json();
					const known = new Set( entries.map( ( e ) => String( e.id ) ) );

					for ( const e of ( Array.isArray( more ) ? more : ( more.fields ?? [] ) ) ) {

						const id = String( e.id ?? '' );

						if ( id === '' || known.has( id ) )
							continue;

						entries.push( { ...e, source: 'protected' } );
						this._bases.set( id, this._protectedBaseUrl );

					}

				}

			} catch ( e ) {
				// Without the add-on, or with a damaged index of it: the first root alone.
			}

		}

		this._index = entries;

		return this._index;

	}

	/**
	 * One field's full detail (cardinality per profile, xpath, applicable rules cross-reference, ...).
	 *
	 * ⚠️ CALL index() FIRST. Only index() learns which fields live in the second root; before it, every
	 * id is fetched from the first root. The field browser always does, because it opens a detail from a
	 * row of the index.
	 *
	 * @param {string} id
	 * @return {Promise<object>}
	 */
	async field( id ) {

		if ( this._fields.has( id ) )
			return this._fields.get( id );

		const base = this._bases.get( String( id ) ) ?? this._baseUrl;

		const promise = this._details( base ).then( ( all ) => {
			const field = all[ id ];
			if ( ! field ) throw new Error( `FieldCatalogClient: field ${ id } not in ${ base }/details.json` );
			return field;
		} );

		this._fields.set( id, promise );

		return promise;

	}

	/**
	 * The detail file of one root, `details.json`, loaded once per root.
	 *
	 * ⚠️ **ONE FILE PER ROOT, NOT ONE FILE PER FIELD** ( since 02.11.2026, `build-field-catalog.php` ). The single files made
	 * 1,274 files on every server and in every ZIP. The add-on Extended Profiles writes its own `details.json` into its
	 * folder in the same form, so the second root needs nothing of its own here.
	 *
	 * @param {string} base
	 * @return {Promise<object>} field id -> full detail
	 */
	_details( base ) {

		this._roots = this._roots || new Map();

		if ( ! this._roots.has( base ) )
			this._roots.set( base, fetch( `${ base }/details.json` ).then( ( res ) => {
				if ( ! res.ok ) throw new Error( `FieldCatalogClient: ${ base }/details.json failed (${ res.status })` );
				return res.json();
			} ).then( ( data ) => ( data && data.fields ) || {} ) );

		return this._roots.get( base );

	}

}
